import logo from './Favicon.png';
import './App.css';
import Ztext from 'react-ztext';
import AnimatedCursor from "react-animated-cursor";
import React, { Suspense, useEffect, useState, useRef } from 'react'
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { useSpring, animated as a } from '@react-spring/web';
import Scene from './Scene';
import kinsCover from './images/Kins.png';
import keenCover from './images/Keen.png'; // Import the Keen image
import hingeCover from './images/Hinge.png'; // Import the Hinge image
import kincaidCover from './images/Kincaid.png'; // Import the Kincaid image
import handScrollCover from './images/HandScroll.png'; // Import the HandScroll image
import ImageHoverEffect from './ImageHoverEffect';
import GooeySkillItem from './GooeySkilItem';
import GooeyLink from './GooeyLink';
import geistsCover from './images/Geists.png'; // Import the Geists image
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import DiaTeaCeremony from './DiaTeaCeremony'; // Import the new component
import Kins from './Kins'; // Import the new component
import Keen from './Keen'; // Import the new component
import HingeHealth from './HingeHealth'; // Import the new component
import { useTheme } from './ThemeContext';


const DrawingCanvas = () => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastPosition, setLastPosition] = useState({ x: 0, y: 0 });
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    // Set canvas dimensions to cover from divider to bottom of page
    const resizeCanvas = () => {
      // Get the footer's position and dimensions
      const footer = document.getElementById('find-me-section');
      if (!footer) return;
      
      // Set canvas width to match footer width
      canvas.width = footer.offsetWidth;
      
      // Set canvas height to extend from top of footer to bottom of viewport
      const footerTop = footer.getBoundingClientRect().top;
      const viewportHeight = window.innerHeight;
      canvas.height = viewportHeight - footerTop + 100; // Add extra 100px to ensure it goes beyond viewport
      
      // Set default styles
      context.lineJoin = 'round';
      context.lineCap = 'round';
      context.lineWidth = 5;
      context.strokeStyle = '#FF8A00'; // Orange color
    };
    
    // Initial resize and setup
    setTimeout(resizeCanvas, 100); // Small delay to ensure DOM is ready
    
    // Handle window resize
    window.addEventListener('resize', resizeCanvas);
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);
  
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setIsDrawing(true);
    setLastPosition({ x, y });
  };
  
  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    context.beginPath();
    context.moveTo(lastPosition.x, lastPosition.y);
    context.lineTo(x, y);
    context.stroke();
    
    setLastPosition({ x, y });
  };
  
  const stopDrawing = () => {
    setIsDrawing(false);
  };
  
  return (
    <canvas
      ref={canvasRef}
      onMouseDown={startDrawing}
      onMouseMove={draw}
      onMouseUp={stopDrawing}
      onMouseLeave={stopDrawing}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1,
        cursor: 'default',
        background: 'transparent'
      }}
    />
  );
};

function App() {
  const [{ background, color, fill}, set] = useSpring({ background: '#efefef', color: '#000000', fill: '#000000'}, [])
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [canvasLoaded, setCanvasLoaded] = useState(false);
  const navigate = useNavigate();
  
  // Function to scroll to the footer contact section
  const scrollToContact = (e) => {
    e.preventDefault();
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // Add a useEffect to handle window resize events
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Add a ref for the Vimeo player
  const vimeoPlayerRef = useRef(null);
  const vimeoIframeRef = useRef(null);
  
  // Initialize Vimeo player with useEffect
  useEffect(() => {
    // Load the Vimeo Player API script
    const script = document.createElement("script");
    script.src = "https://player.vimeo.com/api/player.js";
    script.async = true;
    
    script.onload = () => {
      // Once the API is loaded, initialize the player
      if (vimeoIframeRef.current) {
        const player = new window.Vimeo.Player(vimeoIframeRef.current);
        vimeoPlayerRef.current = player;
        
        // Set initial time to 12 seconds
        player.setCurrentTime(12);
        
        // Add listener for timeupdate event
        player.on('timeupdate', function(data) {
          // If video passes 45 seconds, go back to 12 seconds
          if (data.seconds >= 45) {
            player.setCurrentTime(12).then(() => {
              // Ensure playback continues after seeking
              player.play();
            }).catch(error => {
              console.error('Error setting time:', error);
            });
          }
        });
      }
    };
    
    document.body.appendChild(script);
    
    // Cleanup function
    return () => {
      if (vimeoPlayerRef.current) {
        vimeoPlayerRef.current.off('timeupdate');
        vimeoPlayerRef.current.destroy();
      }
      document.body.removeChild(script);
    };
  }, []); // Empty dependency array means this runs once on mount
  
  // Add this style block to your component or to your CSS file
  useEffect(() => {
    // Create a style element
    const style = document.createElement('style');
    
    // Add rules for all cover images and video container
    style.innerHTML = `
      .hinge-cover, .kins-cover, .keen-cover, .kincaid-cover, 
      .handscroll-cover, .geists-cover, .video-container {
        cursor: pointer !important;
      }
    `;
    
    // Append to head
    document.head.appendChild(style);
    
    // Clean up on component unmount
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  
  return (
    <Routes>
      <Route path="/" element={<MainContent />} />
      <Route path="/DIA-Japanese-Tea-Ceremony" element={<DiaTeaCeremony />} />
      <Route path="/Kins" element={<Kins />} />
      <Route path="/Keen" element={<Keen />} />
      <Route path="/HingeHealth" element={<HingeHealth />} />
    </Routes>
  );
}

function MainContent() {
  const [{ background, color, fill}, set] = useSpring({ background: '#efefef', color: '#000000', fill: '#000000'}, [])
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [canvasLoaded, setCanvasLoaded] = useState(false);
  const navigate = useNavigate();
  
  
  // Function to scroll to the footer contact section
  const scrollToContact = (e) => {
    e.preventDefault();
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // Add a useEffect to handle window resize events
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Add a ref for the Vimeo player
  const vimeoPlayerRef = useRef(null);
  const vimeoIframeRef = useRef(null);
  
  // Initialize Vimeo player with useEffect
  useEffect(() => {
    // Load the Vimeo Player API script
    const script = document.createElement("script");
    script.src = "https://player.vimeo.com/api/player.js";
    script.async = true;
    
    script.onload = () => {
      // Once the API is loaded, initialize the player
      if (vimeoIframeRef.current) {
        const player = new window.Vimeo.Player(vimeoIframeRef.current);
        vimeoPlayerRef.current = player;
        
        // Set initial time to 12 seconds
        player.setCurrentTime(12);
        
        // Add listener for timeupdate event
        player.on('timeupdate', function(data) {
          // If video passes 45 seconds, go back to 12 seconds
          if (data.seconds >= 45) {
            player.setCurrentTime(12).then(() => {
              // Ensure playback continues after seeking
              player.play();
            }).catch(error => {
              console.error('Error setting time:', error);
            });
          }
        });
      }
    };
    
    document.body.appendChild(script);
    
    // Cleanup function
    return () => {
      if (vimeoPlayerRef.current) {
        vimeoPlayerRef.current.off('timeupdate');
        vimeoPlayerRef.current.destroy();
      }
      document.body.removeChild(script);
    };
  }, []); // Empty dependency array means this runs once on mount
  
  // Add this style block to your component or to your CSS file
  useEffect(() => {
    // Create a style element
    const style = document.createElement('style');
    
    // Add rules for all cover images and video container
    style.innerHTML = `
      .hinge-cover, .kins-cover, .keen-cover, .kincaid-cover, 
      .handscroll-cover, .geists-cover, .video-container {
        cursor: pointer !important;
      }
    `;
    
    // Append to head
    document.head.appendChild(style);
    
    // Clean up on component unmount
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  
  return (
    <a.main style={{background}} >
      <a.div className="App">
        <a.div className="App-navigation" style={{ 
          color,
          position: 'relative',
          width: '100%',
          padding: '24px 36px',
          marginTop: '0',
          transition: 'background 0.3s ease, color 0.3s ease'
        }}>
          <a.span className="name" style={{ color }}>Lou Kishfy</a.span>
          <div className="citySpan" style={{ 
            position: 'absolute', 
            left: '50%', 
            transform: 'translateX(-50%)',
            top: '26px',
            width: 'auto',
            textAlign: 'center'
          }}>
            <a.span style={{ color }}>New York, New York</a.span>
          </div>
          <div className="nav-links">
            <a.span style={{ color }}>
              {/* <GooeyLink href="mailto:louiskishfy@gmail.com?subject=Hey Lou, I would love to check out some of your work!" isDarkMode={isDarkMode}>Work</GooeyLink> */}
            </a.span>
            <a.span className="link-spacing" style={{ color }}>
              {/* <GooeyLink href="/feed" isDarkMode={isDarkMode}>Feed</GooeyLink> */}
            </a.span>
            <a.span className="link-spacing" style={{ 
              color,
              transform: 'translate(16px, -.5px)', 
              display: 'inline-block'  // Needed for transform to work properly
            }}>
              <GooeyLink 
                href="mailto:louis.kishfy@gmail.com?subject=Hey Lou, reaching out to..." 
                isDarkMode={isDarkMode}
                textColor={color}
              >
                Contact Me
              </GooeyLink>
            </a.span>
          </div>
        </a.div>
        <div className="canvasHolder">
          {!canvasLoaded && (
            <div className="loading-spinner">
              <div style={{
                borderColor: `${isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
                borderTopColor: `${isDarkMode ? '#ffffff' : '#000000'}`
              }}></div>
              <span style={{ 
                color: isDarkMode ? '#ffffff' : '#000000',
                fontFamily: 'sans-serif' 
              }}></span>
            </div>
          )}
          <Canvas className="canvas" dpr={[2, 4]} style={{}}>
            <Scene 
              setBg={set} 
              onThemeChange={(newIsDarkMode) => {
                console.log("Theme change callback received:", newIsDarkMode);
                // No need to call toggleDarkMode here as Scene.js already does it
              }} 
              onLoadComplete={() => setCanvasLoaded(true)}
            />
            <OrbitControls enablePan={false} enableZoom={false} maxPolarAngle={Math.PI / 2} minPolarAngle={Math.PI / 2} />
          </Canvas>
        </div>
        <Ztext
          depth="1px"
          direction="both"
          event="pointer"
          eventRotation="1deg"
          eventDirection="default"
          fade={false}
          layers={6}
          perspective="600px"
        >
          <a.header className="App-header" style={{color}}>
            A growth product technologist.
          </a.header>
        
        </Ztext>

        <a.header className="App-subHeader" style={{color, lineHeight: '1.4'}}>
          Your all-in-one triad for driving full-funnel growth and developing new verticals, products, features.
          </a.header>

          {/* <a.div className="App-currently" style={{color}}>
          Currently: Staff Product Designer at<GooeyLink href="https://www.berrystreet.co/" target="_blank">Berry Street</GooeyLink> 
          </a.div> */}
     
        <a.div className="App-caseStudies" style={{ color }}>
          {/* <a.h2 className="subhead">Selected Work</a.h2> */}
          <div className="flex-row-container">
            <div className="work-entry">

            <ImageHoverEffect 
                  src={hingeCover} 
                  alt="Hinge Cover" 
                  className="hinge-cover" 
                  fallback={hingeCover}
                  onClick={() => navigate('/HingeHealth')}
                  style={{ cursor: 'pointer' }}
                />
              {/* <img src={hingeCover} alt="Hinge Cover" className="hinge-cover" /> */}
              <div className="hinge-description">
                <h3>Developing and executing IPO readiness strategy for Hinge Health</h3>
                <p>
                  Seeded new acquisition channels, built new products, and optimized the funnel through experimentation, driving over $30M in incremental revenue.
                </p>
              </div>
            </div>
            <div className="work-entry">
            <ImageHoverEffect 
                  src={kinsCover} 
                  alt="Kins Cover" 
                  className="kins-cover" 
                  fallback={kinsCover}
                  onClick={() => navigate('/kins')}
                  style={{ cursor: 'pointer' }}
                />
              {/* <img src={kinsCover} alt="Kins Cover" className="kins-cover" /> */}
              <div className="kins-description">
                <h3>End-to-end product development for Kins</h3>
                <p>
                Kins is a hybrid digital and in-person virtual physical therapy platform.</p>
              </div>
            </div>
            <div className="work-entry">
            <ImageHoverEffect 
                  src={keenCover} 
                  alt="Keen Cover" 
                  className="keen-cover" 
                  fallback={keenCover}
                  onClick={() => navigate('/keen')}
                  style={{ cursor: 'pointer' }}
                />
              {/* <img src={keenCover} alt="Keen Cover" className="keen-cover" /> */}
              <div className="keen-description">
                <h3>Taking Keen from -1 to 1</h3>
                <p>
                Keen is a medicare platform for multi-lingual brokers.
                </p>
              </div>
            </div>
          </div>
          {/* <div className="flex-row-container">
            <div className="work-entry">
              <img src={kincaidCover} alt="Kincaid Cover" className="kincaid-cover" />
              <div className="kincaid-description">
                <h3>Flesch-Kincaid Figma Plug-In</h3>
                <p>A plug-in for all types of copy.</p>
              </div>
            </div>
            <div className="work-entry">
              <img src={handScrollCover} alt="HandScroll Cover" className="handscroll-cover" />
              <div className="handscroll-description">
                <h3>Interactive Handscroll</h3>
                <p>Interactive chinese hand scroll experience, a permeenant exhibit at the Detroit Institute of Arts</p>
              </div>
            </div>
          </div>        */}
        </a.div>

        {/* Video Showcase Section with Geists entry */}
        <a.div className="App-videoShowcase" style={{ 
          color,
          marginTop: '0px',
          marginBottom: '60px',
          marginLeft: '36px',
          marginRight: '36px',
          paddingRight: '64px',
          width: '100%'
        }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            gap: '20px'
          }}>
            {/* Video entry */}
            <div style={{ flex: '1' }}>
              <div className="video-container" style={{
                position: 'relative',
                paddingBottom: '56.25%',
                height: 0,
                overflow: 'hidden',
                maxWidth: '100%',
                // borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                cursor: 'pointer',
                pointerEvents: 'auto'
              }}>
                <iframe
                  ref={vimeoIframeRef}
                  src="https://player.vimeo.com/video/336296257?autoplay=1&background=1&muted=1"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    pointerEvents: 'none'
                  }}
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title="Featured Project Video"
                ></iframe>
                
                <div 
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 1,
                    cursor: 'pointer'
                  }}
                  onClick={() => navigate('/DIA-Japanese-Tea-Ceremony')}
                />
              </div>
              <div className="video-description" style={{
                marginTop: '20px',
                textAlign: 'left'
              }}>
                <h3>Interactive Japanese Tea Ceremony</h3>
                <p style={{fontSize: '.9em'}}>
                  Software development in Electron, including fiducial tracking and shell scripting for museum automation. The application was programmed to dynamically change the season of the video based on the current date.
                </p>
              </div>
            </div>
            
            {/* Geists entry with hover effect */}
            <div style={{ flex: '1' }}>
              <div 
                className="hover-container"
                style={{ 
                  position: 'relative',
                  cursor: 'pointer' // Add pointer cursor
                }}
              >
                <ImageHoverEffect 
                  src={geistsCover} 
                  alt="Geists Cover" 
                  className="geists-cover" 
                  fallback={geistsCover}
                  onClick={() => window.open('https://apps.apple.com/us/app/ai-advice-personalized-geists/id6737850836', '_blank')}
                  style={{ cursor: 'pointer' }}
                />
                
                {/* Tooltip that appears on hover */}
                <div className="download-tooltip" style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  pointerEvents: 'none', // Prevents tooltip from interfering with hover
                  zIndex: 10
                }}>
                  Download
                </div>
                
                {/* Add CSS to show tooltip on hover */}
                <style jsx>{`
                  .hover-container:hover .download-tooltip {
                    opacity: 1;
                  }
                `}</style>
              </div>
              <div className="geists-description" style={{
                marginTop: '20px',
                textAlign: 'left'
              }}>
                <h3>Geists</h3>
                <p style={{fontSize: '.9em'}}>
                Elevate your aura by playing truth or dare with others anonymously, and get enhanced advice with AI.</p>
              </div>
            </div>
          </div>
        </a.div>
        {/* <div className="svgContainer">
          <a.svg style={{ fill }}>
            <rect width="100%" height="2"></rect>
          </a.svg>
        </div> */}
        {/* <svg style="border:1px solid black" >
          <text x="0" y="0" font-size="20" fill="black" dy="0">
          <tspan x="0" dy=".6em">tspan line 1</tspan>
          <br class="responsive"/>
          <tspan x="0" dy="1.2em">tspan line 2</tspan>
          </text>
        </svg> */}
        {/* <a.div className={`line ${isDarkMode ? 'dark-mode-divider' : ''}`}></a.div> */}
   
        <div className="neon-green-section">
           {/* <a.div className="App-subHeader" style={{color}}>
           <h3>Biography</h3>

        <a.span style={{ fontSize: '1em' }}>
        I'm a systems thinker, driven by solving complex challenges and using the affordances of technology to design sticky experiences. I thrive on hands-on work, a bias for action, and learning through experimentation.

        </a.span>
        </a.div> */}

<a.div id="contact-section" className="footer" style={{ 
          marginTop: '24px',
          paddingTop: '0px',
          paddingBottom: '40px',
          borderTop: background.to(bg => `1px solid ${bg === '#121212' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)'}`),
          color
        }}>
          </a.div>
        <a.div className="App-aboutTwoColumn" style={{ color }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row', // Change to column on mobile
            justifyContent: 'space-between', 
            marginBottom: '40px' 
          }}>
            {/* Biography Section */}
            <div className="bio-section" style={{ 
              flex: isMobile ? '1 1 100%' : '1 1 45%', // Take full width on mobile
              marginRight: isMobile ? '0' : '20px',    // No right margin on mobile
              marginBottom: isMobile ? '20px' : '0'    // Add bottom margin on mobile
            }}>
              <h3>Biography</h3>
              <a.span style={{ fontSize: '1em', display: 'block', marginRight: '36px' }}>
                I'm driven by solving complex challenges and using the affordances of technology to design sticky, delightful experiences that drive growth. I thrive in mission-driven environments where I can be hands-on, maintain a bias for action, and continuously learn through rapid experimentation.
              </a.span>
            </div>

            {/* Education Section */}
            <div className="education-section" style={{ 
              flex: isMobile ? '1 1 100%' : '1 1 45%', // Take full width on mobile
              marginLeft: isMobile ? '0' : '20px'      // No left margin on mobile
            }}>
              <h3>Education</h3>
              <div className="education-content" style={{ marginRight: '20px' }}>
                <div className="education-item">
                  School of the Art Institute of Chicago<br />
                  M.Des Designed Objects '16
                </div>
                <div className="education-item">
                  University of Rhode Island<br />
                  B.S. Ocean Engineering '12
                </div>
              </div>
            </div>
          </div>

          {/* Skillset Section */}
          <div className="bio-row">
            <h3>Skillset</h3>
            <div className="skills-content" style={{ marginRight: '36px', paddingRight: '36px' }}>
              <GooeySkillItem>UX/UI</GooeySkillItem>
              <GooeySkillItem>Lean UX</GooeySkillItem>
              <GooeySkillItem>Qualitative Research</GooeySkillItem>
              <GooeySkillItem>UX Engineering</GooeySkillItem>
              <GooeySkillItem>Product Experimentation</GooeySkillItem>
              <GooeySkillItem>Product Strategy</GooeySkillItem>
              <GooeySkillItem>Rapid Prototyping</GooeySkillItem>
            </div>
          </div>
        </a.div>
        <a.div id="contact-section" className="footer" style={{ 
          marginTop: '24px',
          paddingTop: '0px',
          paddingBottom: '40px',
          borderTop: background.to(bg => `1px solid ${bg === '#121212' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)'}`),
          color
        }}>
          </a.div>
        
        <a.div class="row" style={{color}}><h2 className="subhead">Work Experiences</h2></a.div>
        
        {/* First experience row */}
        <a.div style={{color}} className="experience-row">
          <div className="experience-date">
            Current
          </div>
          <div className="experience-company-group">
            <div className="experience-company">
              <b>Berry Street</b>
            </div>
            <div className="experience-title">
              Staff Product Designer
            </div>
          </div>
          <div className="experience-description">
            <span className="justify">
              At Berry Street, I'm helping folks access better health through nutrition.
            </span>
          </div>
        </a.div>
        
        {/* Second experience row */}
        <a.div style={{color}} className="experience-row">
          <div className="experience-date">
            July 2022 - March 2025
          </div>
          <div className="experience-company-group">
            <div className="experience-company">
              <b>Hinge Health</b>
            </div>
            <div className="experience-title">
              Senior Product Designer, Growth
            </div>
          </div>
          <div className="experience-description">
            <span className="justify">
              Drove over $30 million in incremental revenue through targeted experimentation in acquisition, onboarding, engagement, and retention across web and mobile platforms, directly contributing to the company's profitability and IPO readiness.           
              <span className="br"></span>
              Expanded Hinge Health's market penetration by developing and optimizing new acquisition channels, including SEO and incentivized referral programs — building the organic acquisition funnel from scratch. Redesigned the benefit and employer search search experience to make it easy for all customer segments to sign up for Hinge Health.
            </span>
          </div>
        </a.div>
        
        {/* Third experience row */}
        <a.div style={{color}} className="experience-row">
          <div className="experience-date">
            January 2021 - June 2022
          </div>
          <div className="experience-company-group">
            <div className="experience-company">
              <b>Redesign Health</b>
            </div>
            <div className="experience-title">
              Experience Design Lead
            </div>
          </div>
          <div className="experience-description">
            <span className="justify">
              Took Keen & Kins 0-to-1 in 9 months time, from opportunity to minimum marketable product— GTM strategy, user stories, happy maps, feature roadmaps and prioritization, user testing, technical architecture, defining KPIs/key metrics for Series A funding, service blueprints, no-code prototyping/experimentation, and end-to-end design.
              <span className="br"></span>
              Implemented eng-friendly design system in Figma, including a library of common flows seen across operating companies (onboarding, payments, and more).
              <span className="br"></span>
              Created cross-team product frameworks and processes.
            </span>
          </div>
        </a.div>
        
        {/* Fourth experience row */}
        <a.div style={{color}} className="experience-row">
          <div className="experience-date">
            April 2019 - March 2020
          </div>
          <div className="experience-company-group">
            <div className="experience-company">
              <b>Sub Rosa</b>
            </div>
            <div className="experience-title">
              Senior Creative Technologist
            </div>
          </div>
          <div className="experience-description">
            <span className="justify">
            Developed early-stage functional prototypes for features and experiences.
              <span className="br"></span>
              Created user flows, user stories, diagrams for user interactions, wireframes, mock-ups, hardware/software architecture documents.
              <span className="br"></span>
              Managed a cross-functional team of designers and developers for interactive technologies, websites, and mobile apps.
            </span>
          </div>
        </a.div>
        
        {/* Fifth experience row */}
        <a.div style={{color}} className="experience-row">
          <div className="experience-date">
            January 2017 - April 2019
          </div>
          <div className="experience-company-group">
            <div className="experience-company">
              <b>Tellart</b>
            </div>
            <div className="experience-title">
              Creative Technologist
            </div>
          </div>
          <div className="experience-description">
            <span className="justify">
              Defined launch strategy for multiple interactive storytelling products, led technology production in the Providence office.
              <span className="br"></span>
              Led user testing, technical discovery, and implementation of emerging technologies including AR, VR, machine learning, and computer vision. Developed comprehensive technical specifications and user documentation to ensure seamless maintenance and knowledge transfer.</span>
          </div>
        </a.div>
        
        {/* Sixth experience row */}
        <a.div style={{color}} className="experience-row">
          <div className="experience-date">
            June 2010 - May 2014
          </div>
          <div className="experience-company-group">
            <div className="experience-company">
              <b>Blackstone Pharmacy</b>
            </div>
            <div className="experience-title">
              Co-Founder
            </div>
          </div>
          <div className="experience-description">
            <span className="justify">
              Developed and deployed a mobile pharmacy service which sent technicians to customers homes in-person or virtually to monitor compliance and work clinically. All customers that used the service stayed with the pharmacy.
              <span className="br"></span>
              Managed 340b program — tracked prescriptions filled, inventory, dispensing fees, cost of medication and other pertinent information.
              <span className="br"></span>
              Opened second pharmacy location — handled retail design, build, and setting up contracts with PBMs and insurance companies.
              <span className="br"></span>
              Success of clinical consulting and management programs on customer growth and retention led to a profitable acquisition by CVS.
            </span>
          </div>
        </a.div>

        {/* Full-width divider before footer */}
        <a.div style={{ 
          width: 'calc(100% + 72px)', 
          height: '1px', 
          backgroundColor: background.to(bg => bg === '#121212' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)'),
          margin: '60px -36px 0px', 
          position: 'relative'
        }}></a.div>

        {/* Footer */}
        <a.div className="footer" style={{ 
          marginTop: '40px',
          paddingTop: '0px',
          paddingBottom: '40px',
          paddingLeft: '36px',  // Remove any left padding to respect the page's margin
          color
        }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            gap: '20px',
            marginBottom: '20px',
            marginLeft: '0px'  // Ensure no additional left margin
          }}>
            <h3 style={{ 
              fontSize: '24px', 
              fontWeight: '600', 
              marginBottom: '16px',
              marginLeft: '0px'  // Ensure no additional left margin
            }}>Find Me</h3>
            
            {/* Contact links with consistent left alignment */}
            <div style={{ textAlign: 'left', display: 'inline-block', marginLeft: '0px' }}>
              <GooeyLink 
                href="mailto:louiskishfy@gmail.com?subject=Hey Lou, reaching out to..." 
                isDarkMode={isDarkMode}
                textColor={color}
                style={{ textAlign: 'left', display: 'inline-block' }}
              >
                <span style={{ display: 'block', textAlign: 'left' }}>Email</span>
              </GooeyLink>
            </div>
            <div style={{ textAlign: 'left', display: 'inline-block', marginLeft: '0px' }}>
              <GooeyLink 
                href="https://www.linkedin.com/in/lou-kishfy-29441843/" 
                target="_blank" 
                isDarkMode={isDarkMode}
                textColor={color}
                style={{ textAlign: 'left', display: 'inline-block' }}
              >
                <span style={{ display: 'block', textAlign: 'left' }}>LinkedIn</span>
              </GooeyLink>
            </div>
            <div style={{ textAlign: 'left', display: 'inline-block', marginLeft: '0px' }}>
              <GooeyLink 
                href="https://github.com/lkishfy" 
                target="_blank" 
                isDarkMode={isDarkMode}
                textColor={color}
                style={{ textAlign: 'left', display: 'inline-block' }}
              >
                <span style={{ display: 'block', textAlign: 'left' }}>Github</span>
              </GooeyLink>
            </div>
            <div style={{ textAlign: 'left', display: 'inline-block', marginLeft: '0px' }}>
              <GooeyLink 
                href="https://www.figma.com/@LouKishfy" 
                target="_blank" 
                isDarkMode={isDarkMode}
                textColor={color}
                style={{ textAlign: 'left', display: 'inline-block' }}
              >
                <span style={{ display: 'block', textAlign: 'left' }}>Figma</span>
              </GooeyLink>
       
            </div>
            <div style={{ textAlign: 'left', display: 'inline-block', marginLeft: '0px' }}>
              <GooeyLink 
                href="https://x.com/louiskishfy" 
                target="_blank" 
                isDarkMode={isDarkMode}
                textColor={color}
              >
                X
              </GooeyLink>
            </div>
          </div>
          <div style={{ 
            marginTop: '30px', 
            marginLeft: '0px',  // Ensure no additional left margin
            textAlign: 'left',
            fontSize: '14px',
            opacity: 0.7,
            width: '100%',
            position: 'relative',
            zIndex: 2
          }}>
            <a.p style={{ color, textAlign: 'left', marginLeft: '0px' }}>© {new Date().getFullYear()} Lou Kishfy. All rights reserved.</a.p>
          </div>
        </a.div>
        </div>
      </a.div>
    </a.main>
  );
}

export default App;

